import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, map, Subject } from 'rxjs';
import { HelperService } from 'src/app/services/helper.service';

@Component({
    selector: 'app-scrollable-multiple-search-field',
    templateUrl: './scrollable-multiple-search-field.component.html',
    styleUrls: ['./scrollable-multiple-search-field.component.scss'],
    standalone: false
})
export class ScrollableMultipleSearchFieldComponent implements OnInit {
  @Input() dataService: any;
  @Input() dataServiceFunctionName: string = 'list';
  @Input() sortBy = '';
  @Input() sortOrder = '';
  @Input() noOfRecPerPage: number = 20;
  @Input() pageKey: string = 'per_page';
  @Input() searchKey: string = 'search';
  @Input() searchPlaceHolder = 'Type to search';
  @Input() labelKey = 'title';
  @Input() defaultValue = '';
  @Input() messageNoEntriesFound = 'No results found!';
  @Input() addButtonLabel = 'Add Customer';
  @Input() formDataMapper: any = {}; // send as {id:id, name:company_name}
  @Input() keyNameToShowWhenSelected = 'name';
  @Input() inputVal: any = '';
  @Input() showError = true;
  @Input() markCtrlTouched: boolean | undefined = false;
  @Input() addNewOnNoEntriesFound: boolean = false;
  @Input() moduleId: any
  @Input() referenceId: any
  @Input() disabled: boolean = false
  @Input() additionalParams: any = {};
  @Input() multiple: boolean = true

  @Input() filteredResult = [];
  isLoading = false;


  @Output() selectedValueChange = new EventEmitter();
  @Output() selectedValueMultipleChange = new EventEmitter();
 
  @Output() selectedValueClear: EventEmitter<boolean> = new EventEmitter<boolean>(false);

  @Output() addButtonCallback = new EventEmitter();

  ctrl: FormControl = new FormControl();
  // kept purely for touched/error state (markCtrlTouched / hasError)
  selectSearchCtrl: FormControl = new FormControl();
  typeahead$ = new Subject<string>();
  searchVal = '';

  lastPage = 1;
  currentPage = 0;
  showAddRecButton = false;

  apiCallSubscription: any;
  changedValue: any;




  constructor(public helperService: HelperService, private cdr: ChangeDetectorRef) { }

  ngOnInit() {
    this.fetchData();
    this.typeahead$
      .pipe(
        distinctUntilChanged(),
        debounceTime(1000),
        map((val: any) => (val ? val.trim() : ''))
      )
      .subscribe((trimmedVal: string) => {
        if (trimmedVal || this.searchVal) {
          this.removeOngoingAPIcall();
          this.resetPagination();
          this.searchVal = trimmedVal;
          this.fetchData();
        }
      });
      this.ctrl.valueChanges.subscribe(value => {
        if (!value) {
          this.selectedValueClear.next(true);// Trigger when cleared
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['markCtrlTouched'] &&
      !changes['markCtrlTouched'].firstChange &&
      changes['markCtrlTouched'].currentValue
    ) {
      this.selectSearchCtrl.markAsTouched();
    }
    if (changes['disabled']) {
      this.disabled ? this.ctrl.disable() : this.ctrl.enable();
    }
    if (changes['inputVal']?.currentValue) {
      this.inputVal = changes['inputVal']?.currentValue

      // The options may already be loaded. `fetchData()` applies `inputVal` only inside its
      // own response handler, so a value that arrives after that would never reach the
      // control — which is what happened to the PO wizard's vendors, resolved two requests
      // after render while the scopes happened to land in time.
      if (!changes['inputVal'].firstChange && this.filteredResult?.length) {
        this.applyInputValSelection();
      }
    }
  }

  /**
   * Select the options matching `inputVal`.
   *
   * Scopes carry no `id`, so they match on `scope_name`; vendors match on `vendor_id`;
   * everything else falls back to `id`.
   */
  private applyInputValSelection(): void {
    // Array only: callers occasionally assign a bare label string (e.g. `scopeName =
    // event.scope_name`), and the id branch below would call .map() on it and throw.
    if (!Array.isArray(this.inputVal) || !this.inputVal.length) {
      return;
    }

    // Check if we're dealing with scopes (they have scope_name instead of id)
    const isScopes = this.inputVal[0].scope_name !== undefined && this.inputVal[0].id === undefined;

    // Check if we're dealing with vendors (they have vendor_id AND vendor_name)
    // Be specific to avoid false positives
    const isVendors = this.inputVal[0].vendor_id !== undefined && this.inputVal[0].vendor_name !== undefined;

    let selectedOptions: any[];

    if (isScopes) {
      // For scopes, match by scope_name since they don't have id field
      selectedOptions = this.filteredResult.filter((option: any) => {
        return this.inputVal.some((inputItem: any) => inputItem.scope_name === option.scope_name);
      });
    } else if (isVendors) {
      // For vendors, match by vendor_id
      const selectedValue = this.inputVal.map((item: any) => Number(item.vendor_id));
      selectedOptions = this.filteredResult.filter((option: any) =>
        selectedValue.includes(Number(option.vendor_id || option.id)));
    } else {
      // Default behavior for other entities (jobs, etc.)
      const selectedValue = this.inputVal.map((item: any) => Number(item.id));
      selectedOptions = this.filteredResult.filter((option: any) => selectedValue.includes(Number(option.id)));
    }

    this.ctrl.setValue(selectedOptions);
  }

  ngOnDestroy(): void { }


  onChange(changedData: any): void {
    const isMultipleSelection = Array.isArray(changedData); // Check if multiple selection is enabled

    if (isMultipleSelection) {
      // Handle multiple selections
      const parsedDataList: any[] = [];
      changedData.forEach((dataItem) => {
        const parsedData: any = {};
        for (const index in this.formDataMapper) {
          parsedData[index] = dataItem[this.formDataMapper[index]];
        }
        parsedDataList.push(parsedData);
      });

      // Manage display value for multiple selections
      this.inputVal = parsedDataList
      // Emit the updated list of selected values
      this.changedValue = parsedDataList;
      this.selectedValueMultipleChange.emit(parsedDataList);
    }
  }




  clearInput() {
    this.selectSearchCtrl.setValue(''); // Clear search input
    this.ctrl.setValue(''); // Clear selected value
    this.inputVal = ''; // Clear display value
    this.changedValue = null; // Clear changed value
    this.selectedValueChange.emit(null); // Emit the cleared value
    this.selectSearchCtrl.markAsTouched(); // Mark as touched for validation
  }

  fetchData(): void {
    if (this.lastPage === this.currentPage || this.isLoading) {
      return;
    }

    this.isLoading = true;
    let data: any = {
      [this.pageKey]: this.noOfRecPerPage,
      [this.searchKey]: this.searchVal,
      loadingService: false,
      page: this.currentPage < this.lastPage ? this.currentPage + 1 : this.lastPage,
    };
    if (this.sortBy) {
      data['sort_by'] = this.sortBy;
    }
    if (this.sortOrder) {
      data['sort_order'] = this.sortOrder;
    }
    if (this.moduleId) {
      data['moduleId'] = this.moduleId
    }
    if (this.referenceId) {
      data['referenceId'] = this.referenceId
    }
    // console.log('additionalParams', data.concat(this.additionalParams));
    data = { ...data, ...this.additionalParams };

    this.apiCallSubscription = this.dataService[this.dataServiceFunctionName](
      data
    ).subscribe((data: any) => {
      this.filteredResult = this.filteredResult.concat(data.data.data ? data.data.data : data.data);
      const metaData = data.data.meta ? data.data.meta : data.meta;
      this.currentPage = metaData?.current_page;
      this.lastPage = metaData?.last_page;
      
      this.applyInputValSelection();

      if (!this.filteredResult || !this.filteredResult?.length) {
        this.showAddRecButton = true;
      }
      this.isLoading = false;
    });
  }

  patchValue(): void {
    // After the data is loaded, patch the value into the form control
    if (this.inputVal && this.inputVal.length > 0) {
      this.ctrl.setValue(this.inputVal);  // Patch value after data is loaded
      this.cdr.detectChanges();  // Manually trigger change detection
    }
  }

  createRecord() {
    this.addButtonCallback.emit();
    // alert('Feature Under Development..');
  }

  hasError() {
    if (!this.showError) {
      return false;
    }
    return (
      (this.selectSearchCtrl.dirty || this.selectSearchCtrl.touched) &&
      !this.changedValue && !this.inputVal
    );
  }

  private resetPagination() {
    this.currentPage = 0;
    this.lastPage = 1;
    this.filteredResult = [];
  }

  private removeOngoingAPIcall() {
    if (this.apiCallSubscription) {
      this.isLoading = false;
      this.apiCallSubscription.unsubscribe();
    }
  }

  isClearIconVisible(): boolean {
    // Handle both single and multiple selection cases
    if (this.multiple) {
      return Array.isArray(this.ctrl.value) && this.ctrl.value.length > 0;
    } else {
      return !!this.ctrl.value; // Check if a value exists for single selection
    }
  }

}
